// Hooks' form (live's `HooksForm`, spec G14-templates): the AI Actor, the line, and in the footer
// the product, the hooks, and the clip's length, resolution and ratio; one tile a picked hook,
// each on live's model for it (the server's recipe).
import { CloseButton, Group, Image, Loader, Menu, Stack, Text, Textarea, Tooltip, UnstyledButton } from "@mantine/core";
import { Info, LibraryBig, Monitor, SmilePlus, Timer } from "lucide-react";
import { useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import { useListProducts } from "../../../api/generated/endpoints/teams/teams";
import type { ExampleRef, ReferenceSource, TemplateSource } from "../../../api/generated/model";
import { VideoTemplateQuote, VideoTemplateRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { AddActorsDialog } from "../../../platform/actors/AddActorsDialog";
import { ActorsComposer } from "../../../platform/actors/composer/ActorsComposer";
import { actorModels } from "../../../platform/actors/composer/GenerateWithText";
import { pickedOfActor, type PickedActor } from "../../../platform/actors/picked";
import { useMakings, type Making } from "../../../platform/actors/use-makings";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { RatioGlyph } from "../../../ui/RatioGlyph";
import { CostChip } from "../TemplateCost";
import classes from "./forms.module.css";
import {
  AUTO,
  clipOptions,
  DEFAULT_RATIO,
  DEFAULT_RESOLUTION,
  MODELS,
  normalized,
  PROP_HOOK,
  type HookId,
} from "./hooks";
import { HooksPicker } from "./HooksPicker";
import {
  ProductTool,
  productView,
  ReuseSwitch,
  StartArrow,
  type ExampleImage,
  type FormExample,
  type TemplateFormProps,
} from "./kit";
import { mainImage } from "./StaticAdForm";

/** Live's words (`constants.ts`). */
const ACTOR_TIP = "AI Actor - select an existing one or generate new one";
const PRODUCT_TIP = "The product this ad is for. Pick it from your team's product list";
const SCRIPT_TIP = "The line your actor delivers. A default script is pre-filled and can be edited manually.";
const SCRIPT_PLACEHOLDER = "Today we're going to talk about this product.";
const DURATION_TIP = "How long the hook video runs.";
const RESOLUTION_TIP = "Sets the output resolution.";
const RATIO_TIP = "Pick your output's aspect ratio";
const HANDOFF = "Your hooks are finishing in My Generations — you can close this panel now.";
const LOADING = "Loading the generation models…";
const LINE_TOO_LONG = "Keep the line under 80 characters";
const OBJECT_MISSING = "Name the object the actor holds";
/** Live's `MAX_SCRIPT_LENGTH`, after trimming, and its object's default. */
const MAX_LINE = 80;
const DEFAULT_OBJECT = "banana";

/** The actor slot: one picked in Add Actors or made by Add AI Actor, or the card's example's. */
type ActorPick = { kind: "actor"; actor: PickedActor } | { kind: "example"; image: ExampleImage };
/** The product: one of the brand's (its main picture goes), or the card's example's picture. */
type ProductPick = { kind: "product"; id: string } | { kind: "example"; image: ExampleImage };

interface Values {
  actor: ActorPick | null;
  product: ProductPick | null;
  hooks: HookId[];
  heldObject: string;
  script: string;
  duration: string;
  resolution: string;
  ratio: string;
}

const EMPTY: Values = {
  actor: null,
  product: null,
  hooks: [],
  heldObject: DEFAULT_OBJECT,
  script: "",
  duration: AUTO,
  resolution: DEFAULT_RESOLUTION,
  ratio: DEFAULT_RATIO,
};

function exampleOf(example: FormExample, source: ReferenceSource | TemplateSource): ExampleImage | null {
  if (source.kind !== "example") return null;
  return { exampleKey: source.key, url: example.urls[source.key] ?? null, name: "Example" };
}

/** The form's values: the card's example's (live's actor and product pictures, The Impact). */
function valuesOf(example: FormExample | null): Values | null {
  const inputs = example?.inputs;
  if (example === null || inputs?.template !== "hooks") return null;
  const actor = exampleOf(example, inputs.actor);
  const product = exampleOf(example, inputs.product);
  return {
    actor: actor === null ? null : { kind: "example", image: actor },
    product: product === null ? null : { kind: "example", image: product },
    hooks: [...inputs.hooks],
    heldObject: inputs.held_object ?? DEFAULT_OBJECT,
    script: inputs.script ?? "",
    duration: inputs.duration == null ? AUTO : String(inputs.duration),
    resolution: inputs.resolution ?? DEFAULT_RESOLUTION,
    ratio: inputs.aspect_ratio ?? DEFAULT_RATIO,
  };
}

const exampleRef = (image: ExampleImage): ExampleRef => ({ kind: "example", key: image.exampleKey });

/**
 * Live's form: "Reuse example" (on fills the card's example, off clears the form; Replicate opens
 * it on), the "AI Actor" tile (From Library opens Add Actors, Add AI Actor the actor composer in
 * the form's place), the line with live's counter, and in the footer the product (its main
 * picture goes), the hooks picker, and the length, resolution and ratio every picked hook's model
 * makes; the round start, off until there is an actor, a product with a picture and a hook, the
 * line and the object fit, and both of live's models are offered. Each field says what is wrong
 * with it (live's form shows none of its words). Once started, live's hand-off line.
 */
export function HooksForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, options, onStarted } = props;
  const catalog = useGetCatalog().data;
  const products = useListProducts(teamId, brandId).data ?? [];
  const [collapsed, setCollapsed] = useState(false);
  const [start] = useState(() => (props.replicate ? valuesOf(example) : null));
  const [reuse, setReuse] = useState(start !== null);
  const [values, setValues] = useState<Values>(start ?? EMPTY);
  const [adding, setAdding] = useState(false);
  const [makingActor, setMakingActor] = useState(false);
  const [makings, setMakings] = useState<Making[]>([]);
  const set = (changes: Partial<Values>) => setValues((current) => ({ ...current, ...changes }));
  useMakings({
    teamId,
    brandId,
    makings,
    onMade: (jobId, made) => {
      setMakings((current) => current.filter((each) => each.jobId !== jobId));
      set({ actor: { kind: "actor", actor: pickedOfActor(made) } });
    },
    onFailed: (jobId) =>
      setMakings((current) => current.map((each) => (each.jobId === jobId ? { ...each, failed: true } : each))),
  });
  const fill = (on: boolean) => {
    const filled = on ? valuesOf(example) : null;
    setReuse(filled !== null);
    setValues(filled ?? EMPTY);
  };
  const videoModels = catalog?.video_models ?? [];
  const missing = catalog === undefined ? null : (MODELS.find((id) => !videoModels.some((each) => each.id === id)) ?? null);
  const clips = clipOptions(videoModels, values.hooks);
  const product = values.product;
  const chosenProduct = product?.kind === "product" ? products.find((each) => each.id === product.id) : undefined;
  // A product of the brand shows its main picture (live's picks one of its pictures, G14).
  const productImage = chosenProduct === undefined ? null : mainImage(chosenProduct);
  const productSource: TemplateSource | null =
    product?.kind === "example"
      ? exampleRef(product.image)
      : productImage === null
        ? null
        : { kind: "asset", asset_id: productImage };
  const actorSource: ReferenceSource | null =
    values.actor === null ? null : values.actor.kind === "example" ? exampleRef(values.actor.image) : values.actor.actor.source;
  const line = values.script.trim();
  const lineTooLong = line.length > MAX_LINE;
  const objectMissing = values.hooks.includes(PROP_HOOK) && values.heldObject.trim() === "";
  const ready =
    actorSource !== null &&
    productSource !== null &&
    values.hooks.length > 0 &&
    !lineTooLong &&
    !objectMissing &&
    makings.length === 0 &&
    missing === null &&
    catalog !== undefined;
  const inputs = !ready
    ? null
    : {
        template: "hooks" as const,
        actor: actorSource,
        product: productSource,
        hooks: values.hooks,
        held_object: values.heldObject,
        script: values.script,
        ...(values.duration === AUTO ? {} : { duration: Number(values.duration) }),
        resolution: values.resolution,
        aspect_ratio: values.ratio,
      };
  const body = inputs === null ? null : { type: "video.template" as const, inputs };
  const request = VideoTemplateRequest.safeParse(body);
  const quote = VideoTemplateQuote.safeParse(body);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });
  if (makingActor && catalog !== undefined) {
    // Live swaps the form for the actor composer; the form keeps its state.
    const close = () => setMakingActor(false);
    return (
      <ActorsComposer
        teamId={teamId}
        brandId={brandId}
        catalog={catalog}
        onStarted={(started) => {
          setMakings([{ jobId: started.id, failed: false }]);
          set({ actor: null });
          close();
        }}
        place={{
          outputs: 1,
          uploadMost: 1,
          onCancel: close,
          onCreated: (made) => {
            const first = made[0];
            if (first !== undefined) set({ actor: { kind: "actor", actor: pickedOfActor(first) } });
            close();
          },
        }}
      />
    );
  }
  const actorImage = values.actor === null ? null : values.actor.kind === "example" ? values.actor.image : values.actor.actor;
  const actorUrl = actorImage === null ? null : "url" in actorImage ? actorImage.url : actorImage.imageUrl;
  const busy = job.start.isPending;
  return (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={example} checked={reuse} onChange={fill} />}
      summary={<Text size="sm">Hooks</Text>}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={4} wrap="nowrap">
            <ProductTool
              teamId={teamId}
              brandId={brandId}
              tip={PRODUCT_TIP}
              view={
                product?.kind === "example"
                  ? { name: "Example", url: product.image.url }
                  : chosenProduct === undefined
                    ? null
                    : productView(chosenProduct)
              }
              chosenId={product?.kind === "product" ? product.id : null}
              disabled={busy}
              onPick={(picked) => set({ product: { kind: "product", id: picked.productId } })}
              onRemove={() => set({ product: null })}
            />
            <HooksPicker
              picked={values.hooks}
              heldObject={values.heldObject}
              options={options}
              disabled={busy}
              onApply={(hooks, heldObject) => {
                const settings = normalized(
                  { duration: values.duration, resolution: values.resolution, ratio: values.ratio },
                  clipOptions(videoModels, hooks),
                );
                set({ hooks, heldObject, ...settings });
              }}
            />
            {catalog === undefined ? (
              <Group gap={6} wrap="nowrap">
                <Loader size="xs" />
                <Text size="xs">{LOADING}</Text>
              </Group>
            ) : (
              <>
                <Choice
                  variant="footer"
                  setting="Duration"
                  icon={<Timer size={16} strokeWidth={1.5} />}
                  tip={DURATION_TIP}
                  width={82}
                        value={values.duration}
                  options={[
                    { value: AUTO, label: "Auto" },
                    ...clips.durations.map((each) => ({ value: String(each), label: `${each}s` })),
                  ]}
                  onChange={(duration) => set({ duration })}
                />
                <Choice
                  variant="footer"
                  setting="Resolution"
                  icon={<Monitor size={16} strokeWidth={1.5} />}
                  tip={RESOLUTION_TIP}
                  width={88}
                        value={values.resolution}
                  options={(clips.resolutions.length > 0 ? clips.resolutions : [values.resolution]).map((each) => ({
                    value: each,
                    label: each,
                  }))}
                  onChange={(resolution) => set({ resolution })}
                />
                <Choice
                  variant="footer"
                  setting="Aspect ratio"
                  icon={<RatioGlyph ratio={values.ratio} />}
                  tip={RATIO_TIP}
                  width={88}
                        value={values.ratio}
                  options={(clips.ratios.length > 0 ? clips.ratios : [values.ratio]).map((each) => ({
                    value: each,
                    label: each,
                    icon: <RatioGlyph ratio={each} />,
                  }))}
                  onChange={(ratio) => set({ ratio })}
                />
              </>
            )}
          </Group>
          <Group gap={8} wrap="nowrap">
            <CostChip estimate={props.template.estimate} outputs={values.hooks.length} />
            <StartArrow canStart={job.canStart} starting={busy} onStart={job.startJob} />
          </Group>
        </Group>
      }
    >
      <Stack gap={12}>
        <Group gap={8}>
          {makings.length > 0 ? (
            <span className={classes.making} aria-label="Making the AI Actor">
              <Loader size="sm" />
              <CloseButton size="xs" aria-label="Drop the AI Actor being made" onClick={() => setMakings([])} />
            </span>
          ) : actorImage === null ? (
            <Menu position="top-start">
              <Menu.Target>
                <Tooltip label={ACTOR_TIP} multiline maw={280} withArrow position="right">
                  <UnstyledButton className={`${classes.addTile} ${classes.hooksActor}`} aria-label="AI Actor">
                    <SmilePlus size={20} strokeWidth={1.5} aria-hidden />
                    <Text size="xs">AI Actor</Text>
                  </UnstyledButton>
                </Tooltip>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<LibraryBig size={16} />} onClick={() => setAdding(true)}>
                  From Library
                </Menu.Item>
                {catalog !== undefined && actorModels(catalog.image_models).length > 0 ? (
                  <Menu.Item leftSection={<SmilePlus size={16} />} onClick={() => setMakingActor(true)}>
                    Add AI Actor
                  </Menu.Item>
                ) : null}
              </Menu.Dropdown>
            </Menu>
          ) : (
            <span className={`${classes.card} ${classes.hooksActor}`}>
              {actorUrl === null ? null : <Image src={actorUrl} alt={actorImage.name} className={classes.portrait} />}
              <CloseButton
                size="xs"
                className={classes.remove}
                aria-label="Remove the AI Actor"
                onClick={() => set({ actor: null })}
              />
            </span>
          )}
        </Group>
        <Stack gap={4}>
          <Group gap={4}>
            <Text size="sm" fw={600}>
              Script
            </Text>
            <Tooltip label={SCRIPT_TIP} multiline maw={280} withArrow>
              <Info size={14} aria-label="About the script" />
            </Tooltip>
          </Group>
          <Textarea
            variant="unstyled"
            aria-label="Script"
            placeholder={SCRIPT_PLACEHOLDER}
            autosize
            minRows={2}
            maxRows={4}
            value={values.script}
            disabled={busy}
            onChange={(event) => set({ script: event.currentTarget.value })}
          />
          <Text size="xs" ta="right" {...(lineTooLong ? { c: "var(--cai-color-text-danger)" } : {})}>
            {values.script.length} / {MAX_LINE}
          </Text>
          {lineTooLong ? (
            <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
              {LINE_TOO_LONG}
            </Text>
          ) : null}
        </Stack>
        {objectMissing ? (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {OBJECT_MISSING}
          </Text>
        ) : null}
        {chosenProduct !== undefined && productImage === null ? (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            This product has no picture yet. Add one on its page first.
          </Text>
        ) : null}
        {missing === null ? null : (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {`The ${missing} model is not available for this team`}
          </Text>
        )}
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        {/* Live's hand-off line: the run is the server's, so the panel may close. */}
        {job.start.isSuccess ? (
          <Text component="output" size="sm">
            {HANDOFF}
          </Text>
        ) : null}
      </Stack>
      {adding ? (
        <AddActorsDialog
          teamId={teamId}
          brandId={brandId}
          chosen={values.actor?.kind === "actor" ? [values.actor.actor] : []}
          max={1}
          onClose={() => setAdding(false)}
          onConfirm={(actors) => {
            const first = actors[0];
            set({ actor: first === undefined ? null : { kind: "actor", actor: first } });
            setAdding(false);
          }}
        />
      ) : null}
    </GlassPanel>
  );
}
